import FileUploadOutlined from "@mui/icons-material/FileUploadOutlined";
import { Alert, Box, Grid, Typography } from "@mui/material";
import { EdmButton } from "atoms";
import EventSelectManyInput from "atoms/Inputs/EventSelectManyInput";
import { convertFileToBase64 } from "misc/dataHelpers";
import { formatBytes } from "misc/formatHelpers";
import MDXEditorInput from "misc/mdx/MDXEditorInput";
import DialogContextFormDirtyChecker from "molecules/DialogContextFormDirtyChecker";
import FileUploadInput from "organisms/FileUploader/FileUploadInput";
import { ContentBlockRecordType, EventBlockRecordType } from "pages/ExternalEmailTemplate/ExternalEmailTemplate.types";
import { FileRecordType } from "pages/SharedRecords.types";
import { Fragment, useEffect, useMemo, useState } from "react";
import { AutocompleteArrayInput, useDataProvider, useNotify, useTranslate } from "react-admin";
import { Accept } from "react-dropzone";
import { useFormContext, useWatch } from "react-hook-form";

import EmailReceiverList from "./EmailReceiverList";
import EmailTemplateAndSubjectInput from "./EmailTemplateAndSubjectInput";
import { ComposeEmailFormContentPropsType } from "./SendEmailDialog.types";

// Keep in sync with GenericFileField.ALLOWED_TYPES in backend file/serializers.py
const ATTACHMENT_ACCEPT: Accept = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/gif": [".gif"],
  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
  "application/vnd.ms-excel": [".xls"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
  "application/vnd.ms-powerpoint": [".ppt"],
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"],
  "application/vnd.oasis.opendocument.text": [".odt"],
  "text/plain": [".txt"],
  "text/csv": [".csv"],
};

const ALLOWED_EXTENSIONS = [
  ...new Set(
    Object.values(ATTACHMENT_ACCEPT)
      .flat()
      .map((ext) => ext.replace(".", "").toUpperCase()),
  ),
].join(", ");

export default function ComposeEmailFormContent(props: ComposeEmailFormContentPropsType) {
  const {
    recipients,
    templates,
    defaultTemplate,
    availableFiles,
    showPreview,
    previewContent,
    withGeneratedDocument,
    recipientOptions,
  } = props;
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const template = useWatch<{ template: number }>({ name: "template" });
  const watchedAttachments = useWatch<{ attachments: string[] }>({ name: "attachments" });
  const { setValue, getValues } = useFormContext();
  const [exceedsSizeLimit, setExceedsSizeLimit] = useState(false);
  const [contentBlocks, setContentBlocks] = useState<ContentBlockRecordType[]>([]);
  const [eventBlocks, setEventBlocks] = useState<EventBlockRecordType[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<FileRecordType[]>([]);
  const [showUpload, setShowUpload] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const selectableFiles = useMemo(() => [...availableFiles, ...uploadedFiles], [availableFiles, uploadedFiles]);

  const handleUpload = async (acceptedFiles: File[]) => {
    setIsUploading(true);

    try {
      const created = await Promise.all(
        acceptedFiles.map(async (file) => {
          const fileData = await convertFileToBase64(file);
          const { data } = await dataProvider.create<FileRecordType>("files", {
            data: { data: fileData, original_filename: file.name, tags: [] },
          });
          return data;
        }),
      );

      setUploadedFiles((prev) => [...prev, ...created]);
      const currentAttachments = (getValues("attachments") as string[]) || [];
      setValue("attachments", [...currentAttachments, ...created.map((f) => f.id.toString())], { shouldDirty: true });
      notify("notification.upload_success", { type: "info" });
    } catch {
      notify("notification.upload_error", { type: "error" });
    }

    setIsUploading(false);
  };

  const handleRejectedFiles = () => {
    notify("notification.unsupported_file_type", { type: "error", messageArgs: { types: ALLOWED_EXTENSIONS } });
  };

  useEffect(() => {
    const selectedTemplate = templates.find((t) => t.id === template);

    if (selectedTemplate) {
      if (selectedTemplate.sender) {
        setValue("sender", selectedTemplate.sender, { shouldDirty: true });
      }
      setValue("subject", selectedTemplate.subject, { shouldDirty: true });
      setContentBlocks(selectedTemplate.content_blocks);
      setEventBlocks(selectedTemplate.event_blocks);

      selectedTemplate.content_blocks.forEach((block) => {
        setValue(`content_blocks[${block.slug}]`, block.content);
      });
    }
  }, [template]);

  // Files uploaded here exist only for this email, so deselecting one deletes it again.
  useEffect(() => {
    if (!watchedAttachments) return;

    const removed = uploadedFiles.filter((f) => !watchedAttachments.includes(f.id.toString()));

    if (removed.length === 0) return;

    setUploadedFiles((prev) => prev.filter((f) => !removed.includes(f)));
    removed.forEach((f) => dataProvider.delete("files", { id: f.id }));
  }, [watchedAttachments]);

  useEffect(() => {
    if (!watchedAttachments) return;

    const selectedFiles = selectableFiles.filter((f) => watchedAttachments.includes(f.id.toString()));
    const totalSize = selectedFiles.reduce((acc, f) => acc + (f.file_size || 0), 0);

    setExceedsSizeLimit(totalSize > 18000000); // 20MB (Brevo limit)
  }, [watchedAttachments, selectableFiles]);

  useEffect(() => {
    if (defaultTemplate) {
      setContentBlocks(defaultTemplate.content_blocks);
    }
  }, [templates]);

  const selectedTemplate = templates.find((t) => t.id === template);

  return (
    <Fragment>
      <DialogContextFormDirtyChecker />
      <Box display={showPreview ? "none" : "block"} width="100%">
        <EmailReceiverList
          recipients={recipients}
          useInvoiceContactTypeInitially={selectedTemplate?.scope == "INVOICE_TEMPLATES"}
          recipientOptions={recipientOptions}
          templateRecipientRole={selectedTemplate?.recipient_role}
          templateId={selectedTemplate?.id}
        />

        <EmailTemplateAndSubjectInput templates={templates} defaultTemplate={defaultTemplate} />

        {contentBlocks.map((block) => (
          <Box key={block.id} mb={3}>
            <Typography sx={{ mb: 0.5 }} variant="caption" color="textPrimary">
              {block.label}
            </Typography>
            <Box sx={{ flexDirection: "column", flex: 1, display: "flex" }}>
              <MDXEditorInput source={`content_blocks[${block.slug}]`} defaultValue={block.content} />
            </Box>
          </Box>
        ))}

        {eventBlocks.map((eventBlock) => (
          <EventSelectManyInput
            key={eventBlock.slug}
            source={`event_blocks[${eventBlock.slug}]`}
            reference="events"
            label={eventBlock.label}
          />
        ))}

        {withGeneratedDocument && (
          <Alert severity="info" sx={{ marginBottom: 1 }}>
            {translate("emailing.with_generated_document")}
          </Alert>
        )}

        {exceedsSizeLimit && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {translate("emailing.total_file_size_exceeded_warning")}
          </Alert>
        )}

        <Box display="flex" alignItems="center" gap={2} mb={4}>
          {selectableFiles.length > 0 && (
            <AutocompleteArrayInput
              label="emailing.attachments"
              source="attachments"
              choices={selectableFiles}
              optionText={(choice: FileRecordType) =>
                `${choice.original_filename}${choice.file_size ? ` (${formatBytes(choice.file_size)})` : ""}`
              }
              helperText={false}
            />
          )}
          {!showUpload && (
            <EdmButton
              label="emailing.upload_attachment"
              onClick={() => setShowUpload(true)}
              sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
              size="medium"
            >
              <FileUploadOutlined />
            </EdmButton>
          )}
        </Box>

        {showUpload && (
          <FileUploadInput
            isLoading={isUploading}
            dropzoneOptions={{ onDrop: handleUpload, onDropRejected: handleRejectedFiles, accept: ATTACHMENT_ACCEPT }}
          />
        )}
      </Box>
      {showPreview && (
        <Grid container my={4}>
          <Grid item xs={12} minHeight={"60vh"}>
            <iframe title="Preview" srcDoc={previewContent} style={{ width: "100%", height: "100%", border: "none" }} />
          </Grid>
        </Grid>
      )}
    </Fragment>
  );
}
